import { useEffect } from 'react';
import { useShallow } from 'zustand/shallow';

import { useFreeTransfers } from './useFreeTransfers';
import { buildCustomTransfersParams } from '@lib/transfers/store/custom-transfers-utils';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';
import { useTransferRecommendations } from '@hooks/useTransferRecommendations';
import { describeTransferRecommendationsRefusal } from '@api-service/recommendations';

export const useCustomTransferSuggestions = () => {
  const {
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited,
    isReady: isFreeTransfersReady
  } = useFreeTransfers();

  // The query runs against the applied snapshot, not the live filter drafts,
  // so tweaking a filter card never refetches by itself: the user re-applies
  // through the suggest/update button in CustomTransfers.
  const {
    showCustomSuggestions,
    excludePlayerIds,
    activePlayerIdsIn,
    disabledPlayerIds,
    appliedFilters
  } = useCustomTransfersStore(
    useShallow((state) => ({
      showCustomSuggestions: state.showCustomSuggestions,
      excludePlayerIds: state.excludePlayerIds,
      activePlayerIdsIn: state.activePlayerIdsIn,
      disabledPlayerIds: state.disabledPlayerIds,
      appliedFilters: state.appliedFilters
    }))
  );
  const restorePlayer = useCustomTransfersStore((state) => state.restorePlayer);

  const hasDisabledPlayers = disabledPlayerIds.length > 0;
  const customTransfersParams = appliedFilters
    ? buildCustomTransfersParams(appliedFilters)
    : {};
  const hasParams = Object.keys(customTransfersParams).length > 0;

  const layoutPlayerIds = customTransfersParams.playerIdsIn?.split(',') ?? [];
  const activePlayerIds = activePlayerIdsIn ? activePlayerIdsIn.split(',') : [];
  const allPlayerIdsIn = [...new Set([...layoutPlayerIds, ...activePlayerIds])]
    .filter(Boolean)
    .join(',');

  const {
    data: transferRecommendations,
    error,
    isLoading,
    isSuccess,
    isError
  } = useTransferRecommendations({
    ...customTransfersParams,
    ...(excludePlayerIds.length > 0 && {
      excludePlayerIds: excludePlayerIds.join(',')
    }),
    ...(allPlayerIdsIn && { playerIdsIn: allPlayerIdsIn }),
    enabled:
      isFreeTransfersReady &&
      showCustomSuggestions &&
      hasParams &&
      !hasDisabledPlayers
  });

  // Pending dismissals disable the query so the frozen list stays on screen,
  // relying on that list staying cached. If it has been removed (committing
  // transfers elsewhere removes inactive recommendation queries), the pending
  // state is orphaned and would deadlock into a permanent loading state, so
  // restore the dismissed players and let the query run. Restoring (rather
  // than resetting) keeps the member's filter cards intact.
  useEffect(() => {
    if (hasDisabledPlayers && !transferRecommendations) {
      disabledPlayerIds.forEach((id) => restorePlayer(id));
    }
  }, [
    hasDisabledPlayers,
    transferRecommendations,
    disabledPlayerIds,
    restorePlayer
  ]);

  return {
    transfers: transferRecommendations?.data.transfers ?? [],
    isLoading,
    isError,
    // Set only when the API refused the request and said why, so a service
    // failure keeps the generic copy instead of borrowing this explanation.
    refusalDescription: describeTransferRecommendationsRefusal(error),
    hasLoaded: isSuccess,
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited
  };
};
